import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { ProfileMenu } from "@/components/ProfileMenu";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Coins, HelpCircle, Menu } from "lucide-react";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import logoDesktopPng from "@/assets/logo-48.png";
import logoDesktopWebp from "@/assets/logo-48.webp";
import logoMobilePng from "@/assets/logo-105.png";
import logoMobileWebp from "@/assets/logo-105.webp";

interface HeaderProps {
  onLogout?: () => void;
}

export const Header = ({ onLogout }: HeaderProps) => {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const { buildPath } = useLocalizedPath();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      logout();
    }
  };

  const NavLinks = ({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) => (
    <>
      <Link to={buildPath('/about')} onClick={onNavigate}>
        <Button variant="ghost" size={mobile ? "default" : "sm"} className={mobile ? "w-full justify-start" : ""}>
          {t('nav.about')}
        </Button>
      </Link>
      <Link to={buildPath('/examples')} onClick={onNavigate}>
        <Button variant="ghost" size={mobile ? "default" : "sm"} className={mobile ? "w-full justify-start" : ""}>
          {t('nav.examples')}
        </Button>
      </Link>
      <Link to={buildPath('/faq')} onClick={onNavigate}>
        <Button variant="ghost" size={mobile ? "default" : "sm"} className={mobile ? "w-full justify-start" : ""}>
          {t('nav.faq')}
        </Button>
      </Link>
      <Link to={buildPath('/help')} onClick={onNavigate}>
        <Button variant="ghost" size={mobile ? "default" : "sm"} className={mobile ? "w-full justify-start" : ""}>
          <HelpCircle className="h-4 w-4 mr-1" />
          {t('nav.help')}
        </Button>
      </Link>
      <Link to={buildPath('/contact')} onClick={onNavigate}>
        <Button variant="ghost" size={mobile ? "default" : "sm"} className={mobile ? "w-full justify-start" : ""}>
          {t('nav.contact')}
        </Button>
      </Link>
      {user && (
        <Link to={buildPath('/estyling')} onClick={onNavigate}>
          <Button size={mobile ? "default" : "sm"} variant="default" className={mobile ? "w-full" : ""}>
            {t('nav.tool')}
          </Button>
        </Link>
      )}
    </>
  );

  return (
    <header className="border-b border-border bg-background sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
        <Link to={buildPath('/')} className="flex items-center">
          {/* Mobile logo - higher resolution for retina */}
          <picture className="sm:hidden">
            <source type="image/webp" srcSet={logoMobileWebp} />
            <img 
              src={logoMobilePng} 
              alt="E-Styling Studio" 
              className="h-10 w-auto" 
              width={488} 
              height={105} 
              loading="lazy"
              decoding="async"
            />
          </picture>
          {/* Desktop logo - optimized size */}
          <picture className="hidden sm:block">
            <source type="image/webp" srcSet={logoDesktopWebp} />
            <img 
              src={logoDesktopPng} 
              alt="E-Styling Studio" 
              className="h-12 w-auto" 
              width={224} 
              height={48} 
              loading="lazy"
              decoding="async"
            />
          </picture>
        </Link>
        
        <nav className="flex items-center gap-1 sm:gap-2">
          {/* Desktop nav links */}
          <div className="hidden sm:flex items-center gap-1">
            <NavLinks />
          </div>

          {/* Language Switcher - always visible */}
          <LanguageSwitcher />

          {/* Auth-dependent content */}
          {!loading && (
            user ? (
              <>
                {/* Credits display - hidden on mobile, shown in ProfileMenu instead */}
                <div className="hidden sm:flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-muted/50">
                  <Coins className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-foreground">
                    {user.credit_balance || 0}
                  </span>
                </div>
                
                {/* Profile menu */}
                <ProfileMenu onLogout={handleLogout} />
              </>
            ) : (
              <>
                <Link to={buildPath('/auth')} className="hidden sm:inline-flex">
                  <Button variant="ghost" size="sm">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to={buildPath('/auth')}>
                  <Button size="sm">
                    {t('nav.getStarted')}
                  </Button>
                </Link>
              </>
            )
          )}

          {/* Mobile menu button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="sm:hidden">
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center p-4 border-b border-border">
                  <span className="font-semibold text-foreground">{t('nav.menu') || 'Meny'}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="flex flex-col gap-2">
                    <NavLinks mobile onNavigate={() => setIsOpen(false)} />
                    
                    {!loading && !user && (
                      <Link to={buildPath('/auth')} onClick={() => setIsOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start">
                          {t('nav.login')}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Header;
